import type { V3 } from "@project-rouge/rg-core";

// Three Daiwa subsidised-housing typology footprints, hand-traced from the
// `15072022_Wohnungsbau gefördert` PDFs in the ingestion archive
// (see docs/ingestion-archive.md §3.2 + §3.3).
//
// Dimensions are approximate — the PDFs label only the facade rhythm
// (1.40 + 1.10 m window widths), not module sizes. We anchored the bays to
// Daiwa's 3.25 × 6.50 m module grid (derived from IFC, see ingestion-archive
// §3.3), so the footprints align with the module width for clean tessellation.
//
// Polygons are listed clockwise for readability; CreatePolyWorld
// anticlockwise-rewinds them at ingest. Y growth here is the building's
// depth direction (matches the world's z-axis at scene time).

export type DaiwaTypologyId = "ecklosung" | "mittelgang" | "punkthaus";

export type DaiwaTypology = {
  id: DaiwaTypologyId;
  label: string;
  description: string;
  polygon: readonly V3[];
  defaultFloors: number;
};

// Ecklösung — L-corner block. Two arms meeting at a 90° angle, each ~22 m
// long × 14 m deep. Bounding box 36 × 36 m.
const ECKLOSUNG_POLY: readonly V3[] = [
  [0, 0, 0],
  [14, 0, 0],
  [14, 0, 22],
  [36, 0, 22],
  [36, 0, 36],
  [0, 0, 36],
];

// Mittelgang — long bar, double-loaded (central corridor). 50 × 14 m.
const MITTELGANG_POLY: readonly V3[] = [
  [0, 0, 0],
  [50, 0, 0],
  [50, 0, 14],
  [0, 0, 14],
];

// Punkthaus — compact point tower. ~22 × 22 m, slightly off-square so it
// reads differently from a default box variant.
const PUNKTHAUS_POLY: readonly V3[] = [
  [0, 0, 0],
  [22, 0, 0],
  [22, 0, 22],
  [0, 0, 22],
];

export const DAIWA_TYPOLOGIES: readonly DaiwaTypology[] = [
  {
    id: "ecklosung",
    label: "Ecklösung",
    description:
      "Corner block (L-form). Two arms meet at the inside corner where the entrance + core sits. Bounding box 36 × 36 m.",
    polygon: ECKLOSUNG_POLY,
    defaultFloors: 5,
  },
  {
    id: "mittelgang",
    label: "Mittelgang",
    description:
      "Central-corridor bar (double-loaded). Apartments line both long sides; corridor down the middle. 50 × 14 m.",
    polygon: MITTELGANG_POLY,
    defaultFloors: 5,
  },
  {
    id: "punkthaus",
    label: "Punkthaus",
    description:
      "Point tower with central core. Compact footprint, taller than the bar/corner types. 22 × 22 m.",
    polygon: PUNKTHAUS_POLY,
    defaultFloors: 8,
  },
];

export function getDaiwaTypology(id: DaiwaTypologyId): DaiwaTypology {
  const t = DAIWA_TYPOLOGIES.find((x) => x.id === id);
  if (!t) throw new Error(`Unknown Daiwa typology: ${id}`);
  return t;
}
